<template>
	<div>
		<div class="buffer large"></div>
		<div>
			<field-errors v-if="submitAttempted" :form="v.form" field="has_variants"></field-errors>
			<div class="input-container">
				<div class="input-checkbox">
					<span class="checkmark-container">
						<label class="checkbox">
							<input type="checkbox" id="has_variants" v-model="form.has_variants">
							<span class="checkmark"></span>
						</label>
					</span>
					<label for="has_variants" class="input-checkbox-text">This product has variants (color, size, etc)</label>
				</div>
			</div>

			{{ /* Single Variants */ }}
			<span v-if="!hasVariants">
				<field-errors v-if="submitAttempted" :form="v.form" field="variants.$each.0.retail_price"></field-errors>
				<div class="input-container">
					<div class="input-title">Retail Price</div>
					<div class="input-box"><masked-input type="text" :mask="priceMask" v-model="variants[0].retail_price" :guide="false" placeholder="$0.00"></masked-input></div>
				</div>
				<field-errors v-if="hasWholesalePricing && submitAttempted" :form="v.form" field="variants.$each.0.wholesale_price"></field-errors>
				<div v-if="hasWholesalePricing" class="input-container">
					<div class="input-title">Wholesale Price</div>
					<div class="input-box"><masked-input type="text" :mask="priceMask" v-model="variants[0].wholesale_price" :guide="false" placeholder="$0.00"></masked-input></div>
				</div>
				<field-errors v-if="tracksInventory && submitAttempted" :form="v.form" field="variants.$each.0.inventory"></field-errors>
				<div v-if="tracksInventory" class="input-container">
					<div class="input-title">Inventory</div>
					<div class="input-box"><input type="text" v-model="variants[0].inventory"></div>
				</div>
				<field-errors v-if="submitAttempted" :form="v.form" field="variants.$each.0.sku"></field-errors>
				<div class="input-container">
					<div class="input-title">SKU/ID</div>
					<div class="input-box"><input type="text" v-model="variants[0].sku"></div>
				</div>
			</span>
		</div>

		<div class="buffer large"></div>

		{{ /* Muilti Variants */ }}
		<div v-if="hasVariants">
			<div class="title-container">
				<div class="title"><span>{{ variantGroups.length }}</span> Required Variant Group{{ variantGroups.length !== 1 ? 's' : '' }}</div>
				<div class="buttons">
					<a href @click.prevent="addVariantGroup" class="button"><div class="hover-title">Add Group</div><div class="fa fa-plus"></div></a>
				</div>
			</div>
			<field-errors v-if="submitAttempted" :form="v.form" field="variant_groups"></field-errors>
			<field-errors v-for="(group, index) in submitAttempted ? variantGroups : []" :key="`variant-group-${ group.id }-error`" :form="v.form" :field="`variant_groups.$each.${ index }.name`" name="Variant Group Name"></field-errors>
			<div v-sortable="{ onUpdate: reorderVariantGroups, handle: '.input-dragger', scroll: true }" class="grid form two">
				<div v-for="(group, index) in variantGroups" :key="`variant-group-${ group.id }`" class="input-container">
					<div class="input-title">Variant Group #{{ index + 1 }}</div>
					<div class="input-box"><input type="text" placeholder="e.g. Color, Size, etc" v-model="group.name"></div>
					<div class="inline-buttons for-input-title">
						<a href v-if="variantGroups.length > 1" @click.prevent="removeVariantGroup(group.id)" tabindex="-1"><span class="fa fa-trash"></span></a>
						<div v-if="variantGroups.length > 1" class="input-dragger"><span class="fa fa-sort"></span></div>
					</div>
				</div>
			</div>
			<div class="title-container">
				<div class="title"><span>{{ variants.length }}</span> Product Variant{{ variants.length !== 1 ? 's' : '' }}</div>
				<div class="buttons"><a href @click.prevent="addVariant" class="button"><div class="hover-title">Add Variant</div><div class="fa fa-plus"></div></a></div>
			</div>
			<div v-sortable="{ onUpdate: reorderVariants, handle: '.input-dragger', scroll: true }" class="grid two form">
				<div v-for="variant in variants" :key="`variant-${ variant.id }`" class="conjoin-input-containers variant-box">
					<div v-for="(group, index) in form.variant_groups" :key="`variant-${ variant.id }-group-${ group.id }`" class="input-container">
						<div class="input-title">{{ group.name || 'Unnamed Group' }}</div>
						<div class="input-box"><input type="text" v-model="variant.groups[ group.id ]"></div>
						<div class="inline-buttons for-input-title">
							<a v-if="index === 0 && variants.length > 1" href @click.prevent="removeVariant(variant.id)" tabindex="-1"><span class="fa fa-trash"></span></a>
							<div v-if="index === 0  && variants.length > 1" class="input-dragger"><span class="fa fa-sort"></span></div>
						</div>
					</div>
					<div class="input-container">
						<!-- :class="{ 'bad-text': v.form.variants.$each[variantIndex].retail_price.$invalid }" -->
						<div class="input-title shrink">Retail</div>
						<div class="input-box"><masked-input type="text" :mask="priceMask" v-model="variant.retail_price" :guide="false" placeholder="$0.00"></masked-input></div>
						<div v-if="hasWholesalePricing" class="input-title">Wholesale</div>
						<div v-if="hasWholesalePricing" class="input-box"><masked-input type="text" :mask="priceMask" v-model="variant.wholesale_price" :guide="false" placeholder="$0.00"></masked-input></div>
					</div>
					<div class="input-container">
						<div class="input-title shrink">SKU/ID</div>
						<div class="input-box"><input type="text" v-model="variant.sku"></div>
						<div v-if="tracksInventory" class="input-title">Inventory</div>
						<div v-if="tracksInventory" class="input-box"><input type="text" v-model="variant.inventory"></div>
					</div>
				</div>
			</div>

			<div v-if="hasVariants" class="form-gap-large"></div>
			<div v-if="hasVariants" class="input-container">
				<div class="input-title">Default Variant</div>
				<div class="input-box"><variant-selectize :form="form" key-prefix="default-variant" v-model="form.default_variant_id"></variant-selectize></div>
				<div class="input-link"><span class="fa fa-exclamation-triangle"></span>Optional</div>
			</div>
			<div v-if="hasVariants" class="form-gap-large"></div>
		</div>
	</div>
</template>

<script>

import _ from 'underscore';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import ProductFormPartMixin from '../mixins/ProductFormPartMixin';
import VariantSelectize from './VariantSelectize';

export default {

	mixins: [ProductFormPartMixin],

	data() {
		return {
			form: this.value,
		};
	},

	computed: {

		variantGroups() {
			return this.form.variant_groups.filter(group => !group.pendingDelete);
		},

		variants() {
			return this.form.variants.filter(variant => !variant.pendingDelete);
		},

		priceMask() {
			let settings = {
				prefix: '$',
				suffix: '',
				decimalSymbol: '.',
				decimalLimit: 2,
				allowDecimal: true,
			};

			return createNumberMask(settings);
		}

	},

	watch: {

		'form.has_variants': function(newValue) {
			if (newValue) {
				if (!(this.form.variant_groups && this.form.variant_groups.length > 0)) {
					// Has variants, no groups specified
					this.addVariantGroup();
				}
			}
		}
	},

	methods: {

		addVariantGroup() {
			if (!this.form.hasOwnProperty('variant_groups') || !_.isArray(this.form.variant_groups)) {
				this.$set(this.form, 'variant_groups', []);
			}

			this.form.variant_groups.push({
				new: true,
				id: _.uniqueId('variant-group'),
			});
		},

		removeVariantGroup(id) {
			const index = _.findIndex(this.form.variant_groups, { id });
			if (index !== -1) {
				if (this.form.variant_groups[ index ].new) {
					this.$delete(this.form.variant_groups, index);
				} else {
					this.$set(this.form.variant_groups[ index ], 'pendingDelete', true);
				}
			}

			if (this.form.variant_groups.length === 0) {
				this.addVariantGroup();
			}
		},

		addVariant() {
			if (!this.form.hasOwnProperty('variants') || !_.isArray(this.form.variants)) {
				this.$set(this.form, 'variants', []);
			}

			this.form.variants.push({
				new: true,
				id: _.uniqueId('variant'),
				groups: {},
			});
		},

		removeVariant(id) {
			const index = _.findIndex(this.form.variants, { id });
			if (index !== -1) {
				if (this.form.variants[ index ].new) {
					this.$delete(this.form.variants, index);
				} else {
					this.form.variants[ index ].pendingDelete = true;
				}
			}

			if (this.form.variants.length === 0) {
				this.addVariant();
			}
		},

		reorderVariantGroups(event) {
			this.form.variant_groups.splice(event.newIndex, 0, this.form.variant_groups.splice(event.oldIndex, 1)[ 0 ]);
		},

		reorderVariants(event) {
			this.form.variants.splice(event.newIndex, 0, this.form.variants.splice(event.oldIndex, 1)[ 0 ]);
		},

	},

	components: {
		VariantSelectize,
	},

};

</script>
